/**
 * Google Analytics 4 (gtag.js) ヘルパー
 *
 * VITE_GA4_MEASUREMENT_ID が設定されている場合のみ計測する。
 * 未設定時は console.debug で済ませてエラーにしない（開発環境での挙動）。
 */

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}

// 本番では VITE_GA4_MEASUREMENT_ID を Workspace → Build Secrets に設定する。
// 未設定の場合は計測しない（Sandbox/開発時の挙動）。
const GA_ID =
  (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ||
  "G-CLLSC02HCJ";

let injected = false;

/** gtag.js を <head> に動的注入する。idempotent。 */
export function initAnalytics(): void {
  if (typeof window === "undefined") return;
  if (injected) return;
  if (!GA_ID) {
    console.debug("[analytics] VITE_GA4_MEASUREMENT_ID 未設定。GA4 計測スキップ");
    return;
  }

  injected = true;

  // 1) loader script
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  // 2) dataLayer + config
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function gtag(...args: any[]) {
    window.dataLayer!.push(args);
  }
  window.gtag = gtag as typeof window.gtag;
  gtag("js", new Date());
  // SPA: ページビューはルーター経由で手動送信するので send_page_view=false
  gtag("config", GA_ID, { send_page_view: false });
}

/** カスタムイベントを送信 */
export function track(
  eventName: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === "undefined") return;
  if (!GA_ID || !window.gtag) {
    console.debug(`[analytics] track(${eventName})`, params);
    return;
  }
  window.gtag("event", eventName, params ?? {});
}

/** ページビューを送信（SPA遷移用） */
export function trackPageView(path: string): void {
  if (typeof window === "undefined") return;
  if (!GA_ID || !window.gtag) {
    console.debug(`[analytics] page_view ${path}`);
    return;
  }
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

/** user_properties を更新（plan, total_generations 等） */
export function setUserProperties(props: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  if (!GA_ID || !window.gtag) {
    console.debug("[analytics] set user_properties", props);
    return;
  }
  window.gtag("set", "user_properties", props);
}

/** ユーザーIDを設定（GA4の user_id ディメンション） */
export function setUserId(userId: string | null): void {
  if (typeof window === "undefined") return;
  if (!GA_ID || !window.gtag) {
    console.debug(`[analytics] set user_id ${userId ?? "(null)"}`);
    return;
  }
  window.gtag("config", GA_ID, { user_id: userId ?? undefined });
}
